import React, { FC, useEffect, useRef, useState } from 'react'
import { FreeMode, Navigation, Thumbs } from 'swiper/modules'
import { Swiper, SwiperClass, SwiperSlide } from 'swiper/react'
import 'swiper/css'

import ImageByWidthDevice from '@/components/ImageByWidthDevice'
import { GalleryType } from '@/config/enum'
import { GalleryMedia } from '@/types/gallery'
import clsxm from '@/utils/clsxm'

import IcChevron from '~/assets/icons/icon-chevron.svg'

interface AlbumCarouselProps {
  data: GalleryMedia[]
}

const AlbumCarousel: FC<AlbumCarouselProps> = ({ data }) => {
  const [thumbsSwiper, setThumbsSwiper] = useState(null)
  const [swiper, setSwiper] = useState<SwiperClass>()
  const [selectImage, setSelectedImage] = useState(0)
  const paginationRef = useRef<HTMLDivElement>(null)

  const handleSlideChange = (swiper: any) => {
    setSelectedImage(swiper.activeIndex)
  }

  useEffect(() => {
    if (swiper?.current) {
      swiper.current?.swiper.slideTo(selectImage)
    }
  }, [selectImage])

  return (
    <>
      <div className='relative flex w-full flex-col items-center justify-center'>
        <Swiper
          className='banner-swiper'
          navigation={true}
          loop={true}
          pagination={{
            clickable: true,
            el: paginationRef.current,
          }}
          onSwiper={(swiper: any) => {
            handleSlideChange(swiper)
            setSwiper(swiper)
          }}
          thumbs={{ swiper: thumbsSwiper }}
          modules={[FreeMode, Navigation, Thumbs]}
          onSlideChange={handleSlideChange}
        >
          {data.map((photo) => (
            <SwiperSlide key={photo.xid}>
              <div className='relative flex h-[229px] w-full flex-col md:h-[333px] lg:h-[624px]'>
                {photo.type === GalleryType.Image && (
                  <>
                    <div className='absolute inset-0' />
                    <ImageByWidthDevice
                      images={photo.imageFiles}
                      alt='Banner Image'
                      className='h-full w-full object-cover object-center'
                    />
                  </>
                )}

                {photo.type === GalleryType.Video && (
                  <iframe
                    src={`https://www.youtube.com/embed/${photo.videoUrl}`}
                    className={clsxm('h-full w-full', !photo.videoUrl && 'bg-dark')}
                  />
                )}
              </div>
              {photo.caption.length > 0 && (
                <div
                  className={clsxm(
                    'description mt-4 break-words text-center !text-xs text-gray-2 lg:mt-6 lg:!text-base'
                  )}
                  dangerouslySetInnerHTML={{ __html: photo.caption }}
                />
              )}
            </SwiperSlide>
          ))}
        </Swiper>

        <div
          className='absolute left-3 top-1/2 z-10 w-fit -translate-y-1/2 lg:-left-7'
          ref={paginationRef}
        >
          <button
            className='flex h-7 w-7 items-center justify-center rounded-full bg-white shadow-image-nav lg:h-15 lg:w-15'
            onClick={() => swiper?.slidePrev()}
          >
            <IcChevron className='fill-dark text-base lg:text-[28px]' />
          </button>
        </div>

        <div
          className='absolute right-3 top-1/2 z-10 w-fit -translate-y-1/2 lg:-right-7'
          ref={paginationRef}
        >
          <button
            className='flex h-7 w-7 items-center justify-center rounded-full bg-white shadow-image-nav lg:h-15 lg:w-15'
            onClick={() => swiper?.slideNext()}
          >
            <IcChevron className='rotate-180 fill-dark text-base lg:text-[28px]' />
          </button>
        </div>
      </div>
      {swiper && data.length && (
        <Swiper
          breakpoints={{
            320: {
              slidesPerView: 4,
              spaceBetween: 5,
            },
            1280: {
              slidesPerView: 4,
              spaceBetween: 16,
            },
          }}
          onSwiper={setThumbsSwiper}
          loop={true}
          freeMode={true}
          watchSlidesProgress={true}
          modules={[FreeMode, Navigation, Thumbs]}
          className='h-full w-full'
        >
          {data.map((photo, idx) => (
            <SwiperSlide key={photo.xid}>
              <button
                className='relative h-full w-full cursor-pointer overflow-hidden rounded-sm lg:rounded-lg'
                onClick={() => setSelectedImage(idx)}
              >
                <div className={clsxm('absolute inset-0', idx !== selectImage && 'bg-gray-3/50')} />

                {photo.type === GalleryType.Image ? (
                  <ImageByWidthDevice
                    images={photo.imageFiles}
                    alt='Carousel Image'
                    className='h-[46px] w-full object-cover object-center md:w-[265px] lg:h-[149px] lg:w-full'
                  />
                ) : (
                  <img
                    src={`https://i.ytimg.com/vi/${photo.videoUrl}/hqdefault.jpg`}
                    alt='Carousel Image'
                    className={clsxm(
                      'h-[46px] w-full object-cover object-center md:w-[265px] lg:h-[149px] lg:w-full',
                      photo.videoUrl === null && 'bg-dark'
                    )}
                  />
                )}
              </button>
            </SwiperSlide>
          ))}
        </Swiper>
      )}
    </>
  )
}

export default AlbumCarousel
